/**
 * 우측 A/B 비교와 승인 (계획서 §13.4).
 *
 * A/B 버튼은 **전략 선택과 계획 승인을 동시에** 의미한다 (§6.4).
 * 인간 가동은 별도 버튼이며, 자동승인 등급이어도 사람이 눌러야 한다 (§6.4).
 *
 * 추천은 Python 이 결정론적으로 정한다. 화면은 그 결과를 보여 줄 뿐이다 (§6.3).
 */

import type { CandidatePayload, RunSnapshot, StrategyCode } from "@/types/api";
import { approvalGradeLabel, formatTime, formatWon, withUnit } from "@/lib/format";

interface Props {
  snapshot: RunSnapshot;
  /** Status Center Agent 의 설명. **추천을 만든 주체가 아니다** (§6.3, §8.2). */
  aiSummary: string | null;
  aiPending: boolean;
  busy: boolean;
  onDecide: (decision: "approve" | "reject", strategy?: StrategyCode) => void;
  onTimer: (action: "hold" | "resume") => void;
  onHumanStart: () => void;
}

const ROWS: { label: string; render: (c: CandidatePayload) => string }[] = [
  { label: "건조기 기동", render: (c) => formatTime(c.dryer_started_at) },
  { label: "READY", render: (c) => formatTime(c.ready_at) },
  { label: "첫 제품 ETA", render: (c) => formatTime(c.first_batch_eta) },
  { label: "최종 완료", render: (c) => formatTime(c.cooldown_completed_at) },
  { label: "HOLD", render: (c) => `${String(c.hold_minutes)}분` },
  { label: "ETA 여유", render: (c) => `${String(c.eta_margin_minutes)}분` },
  { label: "전체 전력량", render: (c) => withUnit(c.total_kwh, " kWh") },
  { label: "총 에너지 비용", render: (c) => formatWon(c.total_cost_won) },
  { label: "시간 변화율", render: (c) => withUnit(c.time_change_pct, "%") },
  { label: "비용 변화율", render: (c) => withUnit(c.cost_change_pct, "%") },
  { label: "최종 변화율", render: (c) => withUnit(c.final_change_pct, "%") },
  { label: "승인등급", render: (c) => approvalGradeLabel(c.approval_grade) },
];

export function StrategyCompare({
  snapshot,
  aiSummary,
  aiPending,
  busy,
  onDecide,
  onTimer,
  onHumanStart,
}: Props): React.JSX.Element {
  const { plan, clock } = snapshot;
  const candidates = plan.candidates;
  const version = plan.plan_version;

  if (!candidates || version === undefined) {
    return (
      <aside className="plan-panel" aria-label="A/B 비교">
        <h2>A/B 비교</h2>
        <p className="hint">고장이 발생하면 A안과 B안을 비교해 보여 줍니다.</p>
      </aside>
    );
  }

  const awaitingApproval = clock.state === "WAIT_PLAN_APPROVAL";
  const canHumanStart = clock.state === "WAIT_HUMAN_START";
  const timer = snapshot.approval_timer_remaining_seconds;

  return (
    <aside className="plan-panel" aria-label="A/B 비교">
      <header>
        <h2>A/B 비교</h2>
        <span className="version">plan v{version}</span>
      </header>

      <table className="compare-table">
        <thead>
          <tr>
            <th scope="col">항목</th>
            {(["A", "B"] as StrategyCode[]).map((code) => (
              <th key={code} scope="col">
                {code}안
                {plan.recommended === code && <span className="recommend-tag">추천</span>}
                {snapshot.selected_strategy === code && <span className="selected-tag">선택</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ROWS.map((row) => (
            <tr key={row.label}>
              <th scope="row">{row.label}</th>
              {(["A", "B"] as StrategyCode[]).map((code) => (
                <td key={code}>{row.render(candidates[code])}</td>
              ))}
            </tr>
          ))}
          <tr>
            <th scope="row">중대변경</th>
            {(["A", "B"] as StrategyCode[]).map((code) => (
              <td key={code}>
                {candidates[code].critical_change ? "⚠ 해당" : "— 해당 없음"}
              </td>
            ))}
          </tr>
        </tbody>
      </table>

      <section className="reasons">
        <h3>
          추천 이유 <span className="source-tag source-rule">규칙</span>
        </h3>
        <p className="hint">
          이 판정은 <strong>AI 가 아니라 Python 규칙</strong>이 만듭니다. 같은 입력이면 언제나
          같은 결과입니다 (§6.3).
        </p>
        <ul>
          {(plan.recommendation_reasons ?? []).map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
        <p className="baseline">무고장 기준선 비용 {formatWon(plan.baseline_cost_won)}</p>
      </section>

      <section className="ai-summary">
        <h3>
          AI 설명 <span className="source-tag source-ai">AI</span>
        </h3>
        <p className="hint">
          Status Center Agent 가 위 수치를 사람 말로 옮긴 것입니다. <strong>추천을 바꾸지
          않습니다</strong> (§8.2).
        </p>
        {aiSummary ? (
          <p className="ai-text">{aiSummary}</p>
        ) : (
          <p className="hint" role="status">
            {aiPending ? "◌ Agent 가 분석 중입니다…" : "아직 설명이 없습니다."}
          </p>
        )}
      </section>

      <section className="approval">
        <h3>승인</h3>
        <p className="approval-state" role="status">
          {snapshot.plan_approved
            ? `승인 완료 — ${snapshot.selected_strategy ?? "?"}안`
            : "승인 대기"}
          {timer !== null && ` · 자동승인까지 ${String(timer)}초`}
        </p>
        <div className="row">
          {(["A", "B"] as StrategyCode[]).map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => {
                onDecide("approve", code);
              }}
              disabled={busy || !awaitingApproval}
            >
              계획 {code} 승인
            </button>
          ))}
        </div>
        <div className="row">
          <button
            type="button"
            className="secondary"
            onClick={() => {
              onDecide("reject");
            }}
            disabled={busy || !awaitingApproval}
          >
            반려
          </button>
          <button
            type="button"
            className="secondary"
            onClick={() => {
              onTimer("hold");
            }}
            disabled={busy || timer === null}
          >
            보류
          </button>
          <button
            type="button"
            className="secondary"
            onClick={() => {
              onTimer("resume");
            }}
            disabled={busy || timer === null}
          >
            재개
          </button>
        </div>
      </section>

      <section className="human-start">
        <h3>설비 가동</h3>
        <p className="hint">
          자동승인 등급이어도 설비 가동은 사람이 눌러야 합니다 (§6.4)
        </p>
        <button
          type="button"
          className="primary"
          onClick={onHumanStart}
          disabled={busy || !canHumanStart}
        >
          가동 승인
        </button>
        {snapshot.plc && (
          <p className="plc-state" role="status">
            PLC message {snapshot.plc.message_id.slice(0, 8)} ·{" "}
            {snapshot.plc.acked ? "✔ ACK 수신" : "◌ 응답 대기"}
          </p>
        )}
      </section>
    </aside>
  );
}
